import {ContactChannel} from './contact-requirement.models';

/**
 * The catalog of contact channels the manager screen can offer — a picker option per {@link ContactChannel}
 * member, kept as data rather than a template branch so the next channel is a one-line change here (plus one
 * new {@link ContactChannel} member): add the wire string + member in `contact-requirement.models.ts`, then
 * one entry to {@link CONTACT_CHANNEL_LABELS} below. Everything that reads the catalog (the picker, the
 * requirement list, the overview table, the employee-detail card) already iterates it generically and needs no
 * further change. This is the ONE place `"telegram"` maps to the German display name "Telegram".
 */
export interface ContactChannelOption {
  value: ContactChannel;
  label: string;
}

/** Display name per channel. Telegram only in v1 (#558); WhatsApp/SMS are the next candidates. */
export const CONTACT_CHANNEL_LABELS: Record<ContactChannel, string> = {
  [ContactChannel.Telegram]: 'Telegram'
};

/** The picker's options, in the catalog's own declaration order. */
export const CONTACT_CHANNEL_OPTIONS: ContactChannelOption[] = (Object.values(ContactChannel) as ContactChannel[])
  .map(value => ({value, label: CONTACT_CHANNEL_LABELS[value]}));

/**
 * A channel's German display name, falling back to its raw value for one this build does not recognise.
 *
 * The parameter is deliberately WIDER than {@link ContactChannel} (#558, quick links): the contact-quick-link
 * wire model carries `channel` as a plain `string`, because a chip for a channel this build has never heard of
 * is still a usable chip — it has a handle and an address. Narrowed to the union, the `??` fallback below was
 * unreachable by construction, i.e. the function documented a behaviour the type made impossible.
 */
export function contactChannelLabel(channel: ContactChannel | string): string {
  return CONTACT_CHANNEL_LABELS[channel as ContactChannel] ?? String(channel);
}
